import React, { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
  authController,
  ChildAuthComponentProps,
} from "../../../../application/auth";
import ReCaptchaProvider from "../../ReCaptchaProvider";
import ErrorContainer from "../../errorContainer";
import ThrottleButton from "../../throttleButton";
import logger from "../../../../utils/logger";
import UseRecaptchaService from "../../../../services/recaptcha.service";

const isRecaptchaEnabled = UseRecaptchaService.isEnabled();

const PasswordlessEmailFormContent: React.FC<ChildAuthComponentProps> = ({
  useEmailState,
  useErrorState,
  useViewState,
}) => {
  const [email, setEmail] = useEmailState;
  const [, setErrorMessage] = useErrorState;
  const [, setViewState] = useViewState;
  const [emailError, setEmailError] = useState<string>("");
  const [isEnterThrottled, setIsEnterThrottled] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const throttleTimeInMs = 1000;

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      try {
        setErrorMessage("");

        let recaptchaToken: string | undefined = undefined;
        if (isRecaptchaEnabled && executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("passwordless_email");
        } else if (isRecaptchaEnabled && !executeRecaptcha) {
          logger.error(
            "reCAPTCHA is enabled but executeRecaptcha is not available",
          );
          return;
        }

        await authController.sendOTPCode(email, recaptchaToken);

        setViewState("otp");
      } catch (error: unknown) {
        if (error instanceof Error) {
          if (error.cause === "email") {
            setEmailError(error.message);

            return;
          }

          setErrorMessage(error.message);
        } else {
          setErrorMessage(error as string);
        }
      }
    },
    [executeRecaptcha, email, setErrorMessage, setViewState],
  );

  const onChangeEmail = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();

      if (!event.target.value) {
        setEmailError("Email is required");
      } else {
        setEmailError("");
      }

      setEmail(event.target.value);
    },
    [setEmail],
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();

        if (!isEnterThrottled) {
          setIsEnterThrottled(true);
          handleSubmit(event as unknown as React.FormEvent);
          setTimeout(() => setIsEnterThrottled(false), throttleTimeInMs);
        }
      }
    },
    [isEnterThrottled, handleSubmit],
  );

  return (
    <>
      <h2 className="auth-header">Get a login code emailed to you</h2>
      <div className="auth-form-container">
        <form className="auth-form">
          <div className="field">
            <label htmlFor="email" className="label">
              Your email:
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email || ""}
              onKeyDown={handleKeyPress}
              onChange={onChangeEmail}
            />
            {emailError && <p className="error-message">{emailError}</p>}
          </div>

          <div className="buttons">
            <ThrottleButton
              throttleTimeInMs={1000}
              type={"button"}
              disabled={!email || isEnterThrottled}
              classList="primary-button"
              onClick={handleSubmit}
            >
              Send me a code
            </ThrottleButton>
          </div>
        </form>

        <ErrorContainer useErrorState={useErrorState} />
      </div>
    </>
  );
};

const PasswordlessEmailForm: React.FC<ChildAuthComponentProps> = (props) => (
  <ReCaptchaProvider>
    <PasswordlessEmailFormContent {...props} />
  </ReCaptchaProvider>
);

export default PasswordlessEmailForm;
